import React, { useState, useRef } from 'react';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  
  // Parse header - handle quoted fields
  const parseRow = (line) => {
    const fields = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ',' && !inQ) { fields.push(cur.trim()); cur = ''; continue; }
      cur += ch;
    }
    fields.push(cur.trim());
    return fields;
  };

  const headers = parseRow(lines[0]);
  return lines.slice(1).map(line => {
    const vals = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  }).filter(r => Object.values(r).some(v => v));
}

export default function CSVImport({ onImported }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const inputRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      setRows(parsed);
      setPreview({ count: parsed.length, sample: parsed.slice(0, 3) });
      setResult(null);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch('/api/admin/csv-import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}`,
        },
        body: JSON.stringify({ rows }),
      });
      const data = await res.json();
      setResult(data);
      setPreview(null);
      setRows([]);
      if (inputRef.current) inputRef.current.value = '';
      if (onImported) onImported();
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '24px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700, color: '#1e2b38', fontFamily: 'system-ui' }}>
        📥 CSV-Import (Webinar-Leads)
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7a8d', fontFamily: 'system-ui' }}>
        Fehlende UTM-Daten werden nur ergänzt, niemals überschrieben.
        Erwartet: Webinar Datum, event_id, Vorname, Nachname, Email, utm_*, sub_id, referrer
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input ref={inputRef} type="file" accept=".csv"
          onChange={e => handleFile(e.target.files[0])}
          style={{ fontSize: '13px', fontFamily: 'system-ui' }} />
        {rows.length > 0 && (
          <button onClick={handleImport} disabled={loading}
            style={{ padding: '8px 20px', background: '#6366f1', color: 'white', border: 'none',
              borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'system-ui' }}>
            {loading ? '⟳ Importiere…' : `${rows.length} Zeilen importieren`}
          </button>
        )}
      </div>

      {preview && (
        <div style={{ marginTop: '16px', background: '#f8fafc', borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7a8d', marginBottom: '8px', fontFamily: 'system-ui' }}>
            Vorschau ({preview.count} Zeilen):
          </div>
          {preview.sample.map((r, i) => (
            <div key={i} style={{ fontSize: '12px', color: '#1e2b38', fontFamily: 'monospace', marginBottom: '4px' }}>
              {r.Email || r.email} — {r.utm_source || '—'} / {r.utm_campaign || '—'} / sub: {r.sub_id || '—'}
            </div>
          ))}
        </div>
      )}

      {result && (
        <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '10px',
          background: result.error ? '#fee2e2' : '#d1fae5', fontFamily: 'system-ui' }}>
          {result.error ? (
            <span style={{ color: '#991b1b', fontSize: '13px' }}>❌ Fehler: {result.error}</span>
          ) : (
            <div style={{ fontSize: '13px', color: '#065f46' }}>
              ✅ <strong>{result.imported}</strong> Leads importiert ·{' '}
              <strong>{result.enriched}</strong> Buchungen angereichert ·{' '}
              {result.skipped > 0 && <span>{result.skipped} übersprungen</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
